import { useEffect } from 'react';
import siteMeta from '../data/siteMeta.json';

// ─────────────────────────────────────────────────────────────────────────────
// useDocumentMeta — one source of truth for a page's SEO tags.
//
// React 19's native <title>/<meta> hoisting works, but does NOT deduplicate
// existing <meta name="...">/<link rel="...">  tags. That means every time a
// second component mounts a metadata tag with the same name/property/rel, the
// DOM ends up with two of them and crawlers pick whichever they feel like.
//
// This hook imperatively upserts exactly ONE tag per attribute key. On unmount
// (route change) we leave the tag in place — the next route's hook overwrites
// it. Simpler and more predictable than trying to make React's declarative
// hoisting behave.
//
// Inputs:
//   { title, description, canonical, ogTitle, ogDescription, ogUrl,
//     twitterTitle, twitterDescription, robots }
// Field semantics (B01D):
//   undefined  → skipped (existing DOM tag untouched)
//   null       → the tag is REMOVED (canonical / ogUrl only) — used by the
//                404 route so an error page never inherits a canonical or
//                og:url that claims equivalence with a real page
//   string     → upserted
// `robots` is upserted like any other meta name. Valid routes pass
// "index, follow" explicitly so error → valid navigation reliably restores
// indexability; the 404 route passes "noindex, follow".
// ─────────────────────────────────────────────────────────────────────────────
const upsertMeta = (attr, key, value) => {
  if (value === undefined) return;
  const sel = `meta[${attr}="${key}"]`;
  if (value === null) {
    document.head.querySelectorAll(sel).forEach((el) => el.remove());
    return;
  }
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  // Kill any duplicates left over from earlier mounts / static HTML.
  const dupes = document.head.querySelectorAll(sel);
  dupes.forEach((d) => { if (d !== el) d.remove(); });
};

const upsertCanonical = (href) => {
  if (href === undefined) return;
  const sel = 'link[rel="canonical"]';
  if (href === null) {
    document.head.querySelectorAll(sel).forEach((el) => el.remove());
    return;
  }
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  const dupes = document.head.querySelectorAll(sel);
  dupes.forEach((d) => { if (d !== el) d.remove(); });
};

const useDocumentMeta = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  twitterTitle,
  twitterDescription,
  robots,
}) => {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertCanonical(canonical);
    upsertMeta('property', 'og:title', ogTitle || title);
    upsertMeta('property', 'og:description', ogDescription || description);
    // ogUrl === null must win over the canonical fallback — an error page
    // passes BOTH as null so nothing claims a URL that isn't this page.
    upsertMeta('property', 'og:url', ogUrl === null ? null : (ogUrl || canonical));
    upsertMeta('name', 'twitter:title', twitterTitle || title);
    upsertMeta('name', 'twitter:description', twitterDescription || description);
    upsertMeta('name', 'robots', robots);
    upsertMeta('property', 'og:site_name', 'FairFix Construction');
    upsertMeta('property', 'og:locale', 'en_AU');
    upsertMeta('property', 'og:image', `${siteMeta.origin}${siteMeta.image}`);
    upsertMeta('property', 'og:image:alt', siteMeta.imageAlt);
    upsertMeta('name', 'twitter:image', `${siteMeta.origin}${siteMeta.image}`);
    upsertMeta('name', 'twitter:image:alt', siteMeta.imageAlt);
    // No cleanup: the next page's mount will overwrite. Leaving the tags in
    // place means bots that crawl mid-nav still see something sensible.
  }, [title, description, canonical, ogTitle, ogDescription, ogUrl, twitterTitle, twitterDescription, robots]);
};

export default useDocumentMeta;
